import axios from 'axios';
import { getToken, getUser, removeToken, removeUser } from './utils/tenantStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 for authenticated endpoints, not login attempts
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      // If token is already gone, logout was intentional — React handles the UI
      if (!getToken()) {
        return Promise.reject(error);
      }
      const tenantSlug = getUser()?.tenantSlug;
      removeToken();
      removeUser();
      window.location.href = tenantSlug ? `/${tenantSlug}` : '/';
    }
    return Promise.reject(error);
  }
);

// Public
export const submitDemoRequest = (data) => api.post('/demo-request', data);

// Auth
export const getTenantBySlug = (slug) => api.get(`/auth/tenants/${slug}`);  // Validate tenant by slug
export const login = (credentials) => api.post('/auth/login', credentials);
export const signup = (data) => api.post('/auth/signup', data);
export const getSignupEnabled = () => api.get('/auth/signup-enabled');  // public: is self-serve signup open?
export const getCurrentUser = () => api.get('/auth/me');
export const changePassword = (data) => api.post('/auth/change-password', data);
export const getUsers = () => api.get('/auth/users');
export const createUser = (data) => api.post('/auth/users', data);
export const updateUser = (id, data) => api.put(`/auth/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/auth/users/${id}`);
export const resetUserPassword = (id, newPassword) => api.post(`/auth/users/${id}/reset-password`, { newPassword });
export const logoutApi = () => api.post('/auth/logout');
export const unlockUser = (id) => api.post(`/auth/users/${id}/unlock`);

// User Profile (self-service)
export const getUserProfile = () => api.get('/auth/profile');
export const updateUserProfile = (data) => api.put('/auth/profile', data);

// Manager - Managed Employees
export const getManagedEmployees = (userId) => api.get(`/auth/users/${userId}/managed-employees`);
export const setManagedEmployees = (userId, employeeIds) => api.put(`/auth/users/${userId}/managed-employees`, { employeeIds });
export const getMyManagedEmployees = () => api.get('/auth/me/managed-employees');

// Manager Permissions
export const getManagerPermissions = (userId) => api.get(`/auth/users/${userId}/permissions`);
export const setManagerPermissions = (userId, permissions) => api.put(`/auth/users/${userId}/permissions`, { permissions });

// Email Settings (Admin only)
export const getEmailSettings = () => api.get('/auth/settings/email');
export const updateEmailSettings = (data) => api.put('/auth/settings/email', data);
export const testEmailSettings = () => api.post('/auth/settings/email/test');
export const sendPasswordReset = (userId) => api.post(`/auth/users/${userId}/send-password-reset`);

// Notification Settings (Admin only)
export const getNotificationSettings = () => api.get('/auth/settings/notifications');
export const updateNotificationSettings = (data) => api.put('/auth/settings/notifications', data);

// In-App Notifications
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const getNotifications = (limit = 20, offset = 0) => api.get('/notifications/list', { params: { limit, offset } });
export const markNotificationRead = (id) => api.patch(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.patch('/notifications/read-all');

// User Notification Preferences (any user)
export const getMyNotificationPreferences = () => api.get('/auth/me/notification-preferences');
export const updateMyNotificationPreferences = (preferences) => api.put('/auth/me/notification-preferences', { preferences });

// Web Push (per-device subscriptions)
export const getVapidPublicKey = () => api.get('/push/vapid-public-key');
export const subscribePush = (subscription) => api.post('/push/subscribe', { subscription });
export const unsubscribePush = (endpoint) => api.delete('/push/subscribe', { data: { endpoint } });
export const sendTestPush = () => api.post('/push/test');

// Security Settings (Admin only — MFA policy for own tenant)
export const getSecuritySettings = () => api.get('/auth/security-settings');
export const updateSecuritySettings = (data) => api.put('/auth/security-settings', data);

// Forgot Password (public)
export const forgotPassword = (data) => api.post('/auth/forgot-password', data);

// MFA
export const getMfaStatus = () => api.get('/auth/mfa/status');
export const setupMfa = () => api.post('/auth/mfa/setup');
export const verifyMfaSetup = (code) => api.post('/auth/mfa/verify-setup', { code });
export const disableMfa = (data) => api.post('/auth/mfa/disable', data);
export const verifyMfaLogin = (mfaToken, totpCode, backupCode) =>
  api.post('/auth/login/mfa', { mfaToken, totpCode, backupCode });

// Employees
export const getPeople = () => api.get('/employees/people');
export const getEmployees = () => api.get('/employees');
export const getEmployee = (id) => api.get(`/employees/${id}`);
export const createEmployee = (data) => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id, confirmExported = false) => 
  api.delete(`/employees/${id}?confirmExported=${confirmExported}`);
export const toggleEmployeeOvertime = (id, data) => api.patch(`/employees/${id}/overtime`, data);
export const lockoutEmployee = (id, is_active, reason) =>
  api.patch(`/employees/${id}/lockout`, { is_active, reason });
export const downloadW4PDF = (id) => api.get(`/employees/${id}/w4-pdf`, { responseType: 'blob' });

// Pay records by email (plan 076): list what can be sent, then send
export const getEmployeePayRecords = (id) => api.get(`/employees/${id}/pay-records`);
export const sendEmployeePayRecords = (id, data) => api.post(`/employees/${id}/send-pay-records`, data);

// Companies
export const getCompanies = () => api.get('/companies');
export const getRecurringEntrySchedules = () => api.get('/recurring-entries');
export const createRecurringEntrySchedule = (data) => api.post('/recurring-entries', data);
export const updateRecurringEntrySchedule = (id, data) => api.put(`/recurring-entries/${id}`, data);
export const deleteRecurringEntrySchedule = (id) => api.delete(`/recurring-entries/${id}`);
export const runRecurringEntrySchedule = (id, data) => api.post(`/recurring-entries/${id}/run`, data || {});
export const createCompany = (data) => api.post('/companies', data);
export const updateCompany = (id, data) => api.put(`/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/companies/${id}`);

// Bill rate matrix (per-employee-per-company) + company default fallback
export const getBillRateMatrix = () => api.get('/bill-rates/matrix');
export const setBillRateCell = (employee_id, company_id, bill_rate) =>
  api.put('/bill-rates/matrix/cell', { employee_id, company_id, bill_rate });
export const setCompanyDefaultBillRate = (companyId, default_bill_rate) =>
  api.put(`/bill-rates/company/${companyId}/default`, { default_bill_rate });
export const setPayRateCell = (employee_id, company_id, pay_rate) =>
  api.put('/bill-rates/matrix/pay-cell', { employee_id, company_id, pay_rate });
export const setHolidayBillMultiplier = (employee_id, company_id, holiday_bill_multiplier) =>
  api.put('/bill-rates/matrix/holiday-multiplier', { employee_id, company_id, holiday_bill_multiplier });

// Affiliates
// Without scope/company_id: all tenant affiliates regardless of scope.
// With { companyId }: tenant-wide UNION that client's affiliates (used by
//   time-entry dropdowns once a client is picked).
// With { scope: 'null' }: only tenant-wide (unscoped) affiliates.
export const getAffiliates = ({ companyId, scope } = {}) => {
  const params = {};
  if (companyId != null && companyId !== '') params.company_id = companyId;
  if (scope) params.scope = scope;
  const qs = new URLSearchParams(params).toString();
  return api.get(`/affiliates${qs ? `?${qs}` : ''}`);
};
export const createAffiliate = (data) => api.post('/affiliates', data);
export const updateAffiliate = (id, data) => api.put(`/affiliates/${id}`, data);
export const deleteAffiliate = (id) => api.delete(`/affiliates/${id}`);

// Departments
export const getDepartments = (params) => api.get('/departments', { params });
export const createDepartment = (data) => api.post('/departments', data);
export const updateDepartment = (id, data) => api.put(`/departments/${id}`, data);
export const deleteDepartment = (id) => api.delete(`/departments/${id}`);
export const getDepartmentSettings = () => api.get('/departments/settings');
export const updateDepartmentSettings = (data) => api.put('/departments/settings', data);

// Time Entries
export const getTimeEntries = (params) => api.get('/entries', { params });
export const getTimeEntry = (id) => api.get(`/entries/${id}`);
export const createTimeEntry = (data) => api.post('/entries', data);
export const updateTimeEntry = (id, data) => api.put(`/entries/${id}`, data);
export const deleteTimeEntry = (id) => api.delete(`/entries/${id}`);
export const getStatsSummary = (params) => api.get('/entries/stats/summary', { params });
export const markEntryPaid = (id, is_paid, paid_amount) => api.patch(`/entries/${id}/paid`, { is_paid, paid_amount });
export const markEntriesPaid = (entry_ids, is_paid) => api.patch('/entries/bulk/paid', { entry_ids, is_paid });
export const bulkApproveEntries = (entry_ids, approval_status) => api.patch('/entries/bulk/approve', { entry_ids, approval_status });
export const markEntryInvoiced = (id, is_invoiced) => api.patch(`/entries/${id}/invoiced`, { is_invoiced });
export const markEntriesInvoiced = (entry_ids, is_invoiced) => api.patch('/entries/bulk/invoiced', { entry_ids, is_invoiced });
export const updateManagerNotes = (id, manager_notes) => api.patch(`/entries/${id}/manager-notes`, { manager_notes });

// Deduction Templates
export const getDeductionTemplates = (activeOnly) => api.get('/deductions', { params: { active_only: activeOnly } });
export const createDeductionTemplate = (data) => api.post('/deductions', data);
export const updateDeductionTemplate = (id, data) => api.put(`/deductions/${id}`, data);
export const deleteDeductionTemplate = (id) => api.delete(`/deductions/${id}`);

// Employee Deductions
export const getEmployeeDeductions = (employeeId) => api.get(`/deductions/employee/${employeeId}`);
export const assignEmployeeDeduction = (employeeId, data) => api.post(`/deductions/employee/${employeeId}`, data);
export const updateEmployeeDeduction = (employeeId, deductionId, data) => api.put(`/deductions/employee/${employeeId}/${deductionId}`, data);
export const removeEmployeeDeduction = (employeeId, deductionId) => api.delete(`/deductions/employee/${employeeId}/${deductionId}`);

// Payroll Runs
export const getPayrollRuns = (status) => api.get('/payroll', { params: { status } });
export const getPayrollAutomationStatus = () => api.get('/payroll/automation-status');
export const getPayrollRun = (id) => api.get(`/payroll/${id}`);
export const createPayrollRun = (data) => api.post('/payroll', data);
export const calculatePayrollRun = (id, entryIds = [], entryPayOverrides = {}) =>
  api.post(`/payroll/${id}/calculate`, { entryIds, entryPayOverrides });
export const approvePayrollRun = (id) => api.post(`/payroll/${id}/approve`);
export const processPayrollRun = (id) => api.post(`/payroll/${id}/process`);
export const voidPayrollRun = (id, reason) => api.post(`/payroll/${id}/void`, { reason });
export const updatePayrollRun = (id, data) => api.patch(`/payroll/${id}`, data);
export const deletePayrollRun = (id) => api.delete(`/payroll/${id}`);
export const getEligiblePayPeriods = () => api.get('/payroll/eligible-periods');
// Roles
export const getRoles = () => api.get('/roles');
export const getRole = (id) => api.get(`/roles/${id}`);
export const createRole = (data) => api.post('/roles', data);
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);
export const getPermissionRegistry = () => api.get('/roles/permissions');
// Pay Stub Scan
export const scanPayStub = (file) => {
  const formData = new FormData();
  formData.append('paystub', file);
  return api.post('/payroll/scan-paystub', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// YTD Import
export const getYTDImports = () => api.get('/payroll/import-ytd');
export const importYTD = (data) => api.post('/payroll/import-ytd', data);
export const importYTDCSV = (data) => api.post('/payroll/import-ytd/csv', data);
export const deleteYTDImport = (employeeId) => api.delete(`/payroll/import-ytd/${employeeId}`);
export const markPreCutoverPaid = (cutover_date) => api.post('/payroll/import-ytd/mark-paid', { cutover_date });
export const regeneratePayStubs = (runId) => api.post(`/payroll/${runId}/generate-stubs`);
export const getAvailableEntries = (runId) => api.get(`/payroll/${runId}/available-entries`);
export const getStubPreview = (runId, itemId) => api.get(`/payroll/${runId}/items/${itemId}/stub-preview`);
export const downloadPayStubAdmin = (stubId) => downloadFile(`${API_BASE_URL}/payroll/stubs/${stubId}/download`, 'paystub.pdf');

// Tax Filings
export const getTaxFilings = (params) => api.get('/payroll/tax-filings', { params });
export const generate941 = (data) => api.post('/payroll/tax-filings/generate-941', data);
export const generate940 = (data) => api.post('/payroll/tax-filings/generate-940', data);
export const generateW2 = (data) => api.post('/payroll/tax-filings/generate-w2', data);
export const generateAllW2 = (data) => api.post('/payroll/tax-filings/generate-all-w2', data);
export const markTaxFiled = (id) => api.patch(`/payroll/tax-filings/${id}/filed`);
export const downloadTaxFiling = (id) => downloadFile(`${API_BASE_URL}/payroll/tax-filings/${id}/download`, 'tax-filing.pdf');
export const generatePaLocalForm = (data) => api.post('/payroll/tax-filings/generate-pa-local', data);
export const downloadTaxFilingCsv = (id) => downloadFile(`${API_BASE_URL}/payroll/tax-filings/${id}/download?format=csv`, 'tax-worksheet.csv');

// Tax Deposits (plan 117)
export const getTaxDeposits = (params) => api.get('/tax-deposits', { params });
export const getTaxDepositSettings = () => api.get('/tax-deposits/settings');
export const updateTaxDepositSettings = (data) => api.put('/tax-deposits/settings', data);
export const recordTaxDeposit = (data) => api.post('/tax-deposits/payments', data);
export const voidTaxDeposit = (id, data) => api.post(`/tax-deposits/payments/${id}/void`, data);

// Employee Self-Service (My Pay)
export const getMyPayStubs = () => api.get('/payroll/my/stubs');
export const getMyPaySummary = (year) => api.get('/payroll/my/summary', { params: { year } });
export const downloadPayStub = (stubId) => downloadFile(`${API_BASE_URL}/payroll/my/stubs/${stubId}/download`, 'paystub.pdf');

// W-2 electronic delivery (plan 077)
export const getMyW2Consent = () => api.get('/w2-delivery/my/consent');
export const giveMyW2Consent = (data) => api.post('/w2-delivery/my/consent', data);
export const withdrawMyW2Consent = () => api.post('/w2-delivery/my/consent/withdraw');
export const getMyW2s = () => api.get('/w2-delivery/my/w2s');
export const downloadMyW2 = async (id) => {
  const response = await fetch(`${API_BASE_URL}/w2-delivery/my/w2s/${id}/download`, {
    headers: { 'Authorization': `Bearer ${getToken()}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(errorData.error || 'Download failed');
    err.code = errorData.code;
    throw err;
  }
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = 'w2.pdf';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  a.remove();
};
// Saves the sample PDF and returns the X-Sample-Token response header (proof the sample was fetched).
export const downloadW2Sample = async () => {
  const response = await fetch(`${API_BASE_URL}/w2-delivery/my/sample.pdf`, {
    headers: { 'Authorization': `Bearer ${getToken()}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Download failed');
  }
  const sampleToken = response.headers.get('x-sample-token');
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = 'hourtrax-sample-w2.pdf';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  a.remove();
  return sampleToken;
};
export const getW2DeliveryStatus = (taxYear) => api.get('/w2-delivery/admin/status', { params: { tax_year: taxYear } });
export const withdrawW2ConsentAsAdmin = (employeeId, note, receivedOn) => api.post(`/w2-delivery/admin/consents/${employeeId}/withdraw`, { note, received_on: receivedOn });
export const releaseW2s = (taxYear) => api.post('/w2-delivery/admin/release', { tax_year: taxYear });
export const downloadW2DeliveryCsv = (taxYear) => downloadFile(`${API_BASE_URL}/w2-delivery/admin/status.csv?tax_year=${encodeURIComponent(taxYear)}`, `w2-delivery-${taxYear}.csv`);

// Dashboard
export const getDashboardData = () => api.get('/entries/dashboard');
export const getDashboardChartData = (days = 14) => api.get('/entries/dashboard/charts', { params: { days } });

// Export (secure — Authorization header, no token in URL)
export const exportToExcel = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/export/excel?${queryString}`, 'timesheet.xlsx');
};

export const exportSummary = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/export/excel/summary?${queryString}`, 'summary.xlsx');
};

export const exportPayroll = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/export/excel/payroll?${queryString}`, 'payroll.xlsx');
};

// Reports
export const exportOutstandingInvoices = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/outstanding-invoices?${queryString}`, 'outstanding-invoices.xlsx');
};

export const exportCompanyHours = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/company-hours?${queryString}`, 'company-hours.xlsx');
};

export const exportUnpaidBalances = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/unpaid-balances?${queryString}`, 'unpaid-balances.xlsx');
};

export const exportARAging = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/ar-aging?${queryString}`, 'ar-aging.xlsx');
};

export const exportLaborCost = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/labor-cost?${queryString}`, 'labor-cost.xlsx');
};

export const exportMissingTimesheets = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/missing-timesheets?${queryString}`, 'missing-timesheets.xlsx');
};

export const exportProfitMargin = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/profit-margin?${queryString}`, 'profit-margin.xlsx');
};

export const exportBillable = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/billable?${queryString}`, 'billable.xlsx');
};

export const exportWeeklyTimesheet = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/weekly-timesheet?${queryString}`, 'weekly-timesheet.xlsx');
};

export const exportPayPeriodTimesheets = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/reports/pay-period-timesheets?${queryString}`, 'pay-period-timesheets.xlsx');
};

// Report JSON fetchers (for in-page display)
export const fetchPayrollReport = (params) => api.get('/export/excel/payroll', { params: { ...params, format: 'json' } });
export const fetchDetailedTimesheetReport = (params) => api.get('/export/excel', { params: { ...params, format: 'json' } });
export const fetchEmployeeSummaryReport = (params) => api.get('/export/excel/summary', { params: { ...params, format: 'json' } });
export const fetchOutstandingInvoicesReport = (params) => api.get('/reports/outstanding-invoices', { params: { ...params, format: 'json' } });
export const fetchCompanyHoursReport = (params) => api.get('/reports/company-hours', { params: { ...params, format: 'json' } });
export const fetchUnpaidBalancesReport = (params) => api.get('/reports/unpaid-balances', { params: { ...params, format: 'json' } });
export const fetchOutstandingExpensesReport = (params) => api.get('/expenses/export/detailed', { params: { ...params, format: 'json' } });
export const fetchARAgingReport = (params) => api.get('/reports/ar-aging', { params: { ...params, format: 'json' } });
export const fetchLaborCostReport = (params) => api.get('/reports/labor-cost', { params: { ...params, format: 'json' } });
export const fetchMissingTimesheetsReport = (params) => api.get('/reports/missing-timesheets', { params: { ...params, format: 'json' } });
export const fetchProfitMarginReport = (params) => api.get('/reports/profit-margin', { params: { ...params, format: 'json' } });
export const fetchBillableReport = (params) => api.get('/reports/billable', { params: { ...params, format: 'json' } });
export const fetchWeeklyTimesheetReport = (params) => api.get('/reports/weekly-timesheet', { params: { ...params, format: 'json' } });
export const fetchPayPeriodReport = (params) => api.get('/reports/pay-period-timesheets', { params: { ...params, format: 'json' } });

// Employee Hours Lookup
export const getEmployeeHours = (employeeId, params) => 
  api.get(`/entries/employee/${employeeId}/hours`, { params });

// Audit Log (Admin only)
export const getAuditLogs = (params) => api.get('/audit', { params });
export const getAuditActionTypes = () => api.get('/audit/action-types');

// Admin dashboard (action-inbox home)
export const getAdminInbox = (params) => api.get('/dashboard/admin-inbox', { params });

// Global search (⌘K command palette)
export const globalSearch = (q) => api.get('/search', { params: { q } });

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const getInvoiceEntries = (companyId, params) => api.get(`/invoices/entries/${companyId}`, { params });
export const createInvoice = (data) => api.post('/invoices', data);
export const generateInvoicesFromEntries = (entryIds) => api.post('/invoices/generate-from-entries', { entry_ids: entryIds });
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const updateInvoiceStatus = (id, status) => api.patch(`/invoices/${id}/status`, { status });
export const recalculateInvoice = (id) => api.post(`/invoices/${id}/recalculate`);
export const recordInvoicePayment = (id, payload) =>
  api.post(`/invoices/${id}/payment`, payload);
export const listInvoicePayments = (id) => api.get(`/invoices/${id}/payments`);
export const voidInvoicePayment = (invoiceId, paymentId) =>
  api.delete(`/invoices/${invoiceId}/payments/${paymentId}`);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const exportFercInvoice = (id) => downloadFile(`${API_BASE_URL}/invoices/${id}/export-ferc`, `FERC_Invoice_${id}.xlsx`);
export const exportInvoice = (id, format) => {
  const ext = format === 'standard' ? 'pdf' : 'xlsx';
  const prefix = format === 'ferc' ? 'FERC_Invoice' : 'Invoice';
  return downloadFile(`${API_BASE_URL}/invoices/${id}/export?format=${format}`, `${prefix}_${id}.${ext}`);
};

// Payment Pipeline
export const getPipelineSummary = (params) => api.get('/pipeline/summary', { params });
export const getPipelineEntries = (params) => api.get('/pipeline/entries', { params });

// Benefits
export const getBenefitPlans = (activeOnly) => api.get('/benefits/plans', { params: { active_only: activeOnly } });
export const createBenefitPlan = (data) => api.post('/benefits/plans', data);
export const updateBenefitPlan = (id, data) => api.put(`/benefits/plans/${id}`, data);
export const getEnrollmentPeriods = () => api.get('/benefits/enrollment-periods');
export const createEnrollmentPeriod = (data) => api.post('/benefits/enrollment-periods', data);
export const updateEnrollmentPeriod = (id, data) => api.put(`/benefits/enrollment-periods/${id}`, data);
export const getEnrollments = (status, employeeId) => api.get('/benefits/enrollments', { params: { status, employee_id: employeeId } });
export const updateEnrollmentStatus = (id, status) => api.patch(`/benefits/enrollments/${id}/status`, { status });
export const getMyEnrollments = () => api.get('/benefits/my/enrollments');
export const getAvailablePlans = () => api.get('/benefits/available-plans');
export const createEnrollment = (data) => api.post('/benefits/enrollments', data);

// PTO Requests
export const getPTORequests = (params) => api.get('/pto', { params });
export const getMyPTO = () => api.get('/pto/my');
export const getPTOCalendar = (params) => api.get('/pto/calendar', { params });
export const createPTORequest = (data) => api.post('/pto', data);
export const approvePTORequest = (id, notes) => api.patch(`/pto/${id}/approve`, { review_notes: notes });
export const denyPTORequest = (id, notes) => api.patch(`/pto/${id}/deny`, { review_notes: notes });
export const cancelPTORequest = (id) => api.patch(`/pto/${id}/cancel`);
export const cancelPTORemainder = (id) => api.patch(`/pto/${id}/cancel-remainder`);

// Tenant Settings (Company Info)
export const getTenantSettings = () => api.get('/tenant-settings');
export const updateTenantSettings = (data) => api.put('/tenant-settings', data);
export const runAutoInvoice = () => api.post('/tenant-settings/auto-invoice/run');
export const getAutoInvoiceLog = (limit = 50) => api.get(`/tenant-settings/auto-invoice/log?limit=${limit}`);

// Payroll Automation
export const runAutoPayroll = () => api.post('/tenant-settings/auto-payroll/run');
export const getAutoPayrollLog = (limit = 50) => api.get(`/tenant-settings/auto-payroll/log?limit=${limit}`);

// SSO / OIDC Settings
export const getPipelineConfig = () => api.get('/tenant-settings/pipeline');
export const updatePipelineConfig = (data) => api.put('/tenant-settings/pipeline', data);
export const getPipelineStatus = () => api.get('/tenant-settings/pipeline/status');

export const getSSOSettings = () => api.get('/tenant-settings/sso');
export const updateSSOSettings = (data) => api.put('/tenant-settings/sso', data);
// Module switches + employee home screen (tenants.settings.modules / employee_home).
// GET is open to every authenticated user (the nav needs it); PUT needs can_configure_settings
// and accepts a partial body: { modules?: { benefits?, time_off?, payroll? }, employee_home? }.
export const getModuleSettings = () => api.get('/tenant-settings/modules');
export const getFeatures = () => api.get('/tenant-settings/features');
export const updateModuleSettings = (data) => api.put('/tenant-settings/modules', data);
// Per-tenant words for Client / Affiliate / Department / Property (tenants.settings.terminology).
export const getTerminology = () => api.get('/tenant-settings/terminology');
export const updateTerminology = (terminology) => api.put('/tenant-settings/terminology', { terminology });

// Zoho Integration
export const getZohoCredentials = () => api.get('/zoho/credentials');
export const saveZohoCredentials = (data) => api.post('/zoho/credentials', data);
export const getZohoStatus = () => api.get('/zoho/status');
export const getZohoAuthUrl = () => api.get('/zoho/connect');
export const disconnectZoho = () => api.post('/zoho/disconnect');
export const listZohoOrganizations = () => api.get('/zoho/organizations');
export const setZohoOrganization = (organizationId) => api.put('/zoho/organization', { organizationId });
export const pushInvoiceToZoho = (invoiceId) => api.post(`/zoho/sync/push-invoice/${invoiceId}`);
export const pullZohoStatuses = () => api.post('/zoho/sync/pull-statuses');
export const getZohoSyncStatus = () => api.get('/zoho/sync/status');
export const getZohoAutoSync = () => api.get('/zoho/auto-sync');
export const updateZohoAutoSync = (data) => api.put('/zoho/auto-sync', data);
export const previewZohoPull = (options) => api.post('/zoho/sync/preview', options);
export const runZohoPull = (options) => api.post('/zoho/sync/pull', options);
export const listZohoAccounts = () => api.get('/zoho/chart-of-accounts');
export const setZohoDefaultIncomeAccount = (accountId) => api.put('/zoho/default-income-account', { accountId });

// Contacts
export const getCompanyContacts = (companyId) => api.get(`/contacts/company/${companyId}`);
export const getAffiliateContacts = (affiliateId) => api.get(`/contacts/affiliate/${affiliateId}`);
export const createCompanyContact = (companyId, data) => api.post(`/contacts/company/${companyId}`, data);
export const createAffiliateContact = (affiliateId, data) => api.post(`/contacts/affiliate/${affiliateId}`, data);
export const updateContact = (id, data) => api.put(`/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`);

// Expenses
export const getExpenses = (params) => api.get('/expenses', { params });
export const getExpense = (id) => api.get(`/expenses/${id}`);
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const addExpenseItem = (expenseId, data) => api.post(`/expenses/${expenseId}/items`, data);
export const updateExpenseItem = (expenseId, itemId, data) => api.put(`/expenses/${expenseId}/items/${itemId}`, data);
export const deleteExpenseItem = (expenseId, itemId) => api.delete(`/expenses/${expenseId}/items/${itemId}`);
export const uploadReceipt = (expenseId, itemId, file) => {
  const formData = new FormData();
  formData.append('receipt', file);
  return api.post(`/expenses/${expenseId}/items/${itemId}/receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteReceipt = (expenseId, itemId, receiptId) => {
  if (receiptId) {
    return api.delete(`/expenses/${expenseId}/items/${itemId}/receipts/${receiptId}`);
  }
  return api.delete(`/expenses/${expenseId}/items/${itemId}/receipt`);
};
export const openReceipt = async (expenseId, itemId, receiptId) => {
  const token = getToken();
  const url = receiptId
    ? `${API_BASE_URL}/expenses/${expenseId}/items/${itemId}/receipts/${receiptId}`
    : `${API_BASE_URL}/expenses/${expenseId}/items/${itemId}/receipt`;
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to load receipt');
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
  // Revoke after a delay to allow the new tab to load
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
};
export const exportExpenses = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/expenses/export/csv?${queryString}`, 'expenses.csv');
};
export const exportExpensesDetailed = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/expenses/export/detailed?${queryString}`, 'expenses-detailed.xlsx');
};
export const exportExpensePdf = (expenseId) => {
  return downloadFile(`${API_BASE_URL}/expenses/${expenseId}/export/pdf`, 'expense.pdf');
};

// Timesheet Email Schedules
export const getTimesheetSchedules = () => api.get('/timesheet-schedules');
export const getTimesheetSchedule = (id) => api.get(`/timesheet-schedules/${id}`);
export const createTimesheetSchedule = (data) => api.post('/timesheet-schedules', data);
export const updateTimesheetSchedule = (id, data) => api.put(`/timesheet-schedules/${id}`, data);
export const deleteTimesheetSchedule = (id) => api.delete(`/timesheet-schedules/${id}`);
export const sendTestTimesheetEmail = (id) => api.post(`/timesheet-schedules/${id}/test`);
export const previewTimesheetEmail = (id) => api.get(`/timesheet-schedules/${id}/preview`);

// Clock In/Out
export const getClockInStatus = (employeeId) => api.get('/clockin', { params: { employee_id: employeeId } });
export const getActiveClockIns = () => api.get('/clockin/active');
export const clockIn = (data) => api.post('/clockin', data);
export const clockOut = (data) => api.post('/clockin/out', data);
export const getPunchTokens = (count) => api.post('/clockin/punch-tokens', { count });
export const cancelClockIn = (employeeId, clockInId) => api.delete(`/clockin/${employeeId}`, clockInId ? { params: { clock_in_id: clockInId } } : undefined);

// Shift Settings
export const getShiftSettings = () => api.get('/shift-settings');
export const updateShiftSettings = (data) => api.put('/shift-settings', data);

// Time Entry Approvals
export const getPendingApprovals = () => api.get('/entries/pending/approval');
export const getPendingApprovalCount = () => api.get('/entries/pending/count');
export const getPendingPTOCount = () => api.get('/pto/pending/count');
export const approveTimeEntry = (id, notes) => api.patch(`/entries/${id}/approve`, { notes });
export const denyTimeEntry = (id, notes) => api.patch(`/entries/${id}/deny`, { notes });
export const submitTimeEntry = (id) => api.patch(`/entries/${id}/submit`);
export const submitTimeEntries = (entry_ids) => api.patch('/entries/bulk/submit', { entry_ids });

// Tenant Management (program_admin only)
export const getAllTenants = () => api.get('/tenants');
export const getTenant = (id) => api.get(`/tenants/${id}`);
export const createTenant = (data) => api.post('/tenants', data);
export const getTenantProfiles = () => api.get('/tenants/profiles');
export const updateTenant = (id, data) => api.put(`/tenants/${id}`, data);
export const deleteTenant = (id) => api.delete(`/tenants/${id}`);
export const getTenantUsers = (id) => api.get(`/tenants/${id}/users`);
export const getTenantEmailSettings = (id) => api.get(`/tenants/${id}/email-settings`);
export const updateTenantEmailSettings = (id, data) => api.put(`/tenants/${id}/email-settings`, data);
export const resetTenantUserPassword = (tenantId, userId) => api.post(`/tenants/${tenantId}/users/${userId}/reset-password`);
export const getTenantAuthSettings = (id) => api.get(`/tenants/${id}/auth-settings`);
export const updateTenantAuthSettings = (id, data) => api.put(`/tenants/${id}/auth-settings`, data);

// Pay Periods
export const getPayPeriods = (params) => api.get('/pay-periods', { params });
export const getPayPeriod = (id) => api.get(`/pay-periods/${id}`);
export const getCurrentPayPeriod = (date) => api.get('/pay-periods/current', { params: { date } });
export const checkLockedPeriod = (date) => api.get('/pay-periods/check-locked', { params: { date } });
export const createPayPeriod = (data) => api.post('/pay-periods', data);
export const generatePayPeriods = (data) => api.post('/pay-periods/generate', data);
export const updatePayPeriod = (id, data) => api.put(`/pay-periods/${id}`, data);
export const lockPayPeriod = (id) => api.patch(`/pay-periods/${id}/lock`);
export const unlockPayPeriod = (id) => api.patch(`/pay-periods/${id}/unlock`);
export const processPayPeriod = (id, notes) => api.patch(`/pay-periods/${id}/process`, { notes });
export const unprocessPayPeriod = (id) => api.patch(`/pay-periods/${id}/unprocess`);
export const deletePayPeriod = (id) => api.delete(`/pay-periods/${id}`);
export const getPayPeriodEntries = (id, params) => api.get(`/pay-periods/${id}/entries`, { params });
export const getPayPeriodEmployeeSummary = (id) => api.get(`/pay-periods/${id}/employee-summary`);
export const getPayPeriodSettings = () => api.get('/pay-periods/settings');
export const updatePayPeriodSettings = (data) => api.put('/pay-periods/settings', data);

// Holidays
export const getHolidays = (params) => api.get('/holidays', { params });
export const createHoliday = (data) => api.post('/holidays', data);
export const updateHoliday = (id, data) => api.put(`/holidays/${id}`, data);
export const deleteHoliday = (id) => api.delete(`/holidays/${id}`);
export const generateHolidayEntries = (data) => api.post('/holidays/generate-entries', data);

// Holiday Schedules
export const getHolidaySchedules = () => api.get('/holiday-schedules');
export const getFederalHolidayOptions = (year) => api.get('/holiday-schedules/federal-options', { params: { year } });
export const getHolidayScheduleHolidays = (id) => api.get(`/holiday-schedules/${id}/holidays`);
export const createHolidaySchedule = (data) => api.post('/holiday-schedules', data);
export const updateHolidaySchedule = (id, data) => api.put(`/holiday-schedules/${id}`, data);
export const deleteHolidaySchedule = (id) => api.delete(`/holiday-schedules/${id}`);
export const setDefaultHolidaySchedule = (id) => api.post(`/holiday-schedules/${id}/set-default`);

// Pay Period Presets
export const getPayPeriodPresets = () => api.get('/pay-period-presets');
export const getAllPayPeriodPresets = () => api.get('/pay-period-presets/all');
export const createPayPeriodPreset = (data) => api.post('/pay-period-presets', data);
export const updatePayPeriodPreset = (id, data) => api.put(`/pay-period-presets/${id}`, data);
export const deletePayPeriodPreset = (id) => api.delete(`/pay-period-presets/${id}`);

// Custom Reports
export const getCustomReports = () => api.get('/custom-reports');
export const createCustomReport = (data) => api.post('/custom-reports', data);
export const updateCustomReport = (id, data) => api.put(`/custom-reports/${id}`, data);
export const deleteCustomReport = (id) => api.delete(`/custom-reports/${id}`);
export const runCustomReport = (id, filters) => api.post(`/custom-reports/${id}/run`, filters);
export const exportCustomReport = (id, params) => {
  const queryString = new URLSearchParams(params).toString();
  return downloadFile(`${API_BASE_URL}/custom-reports/${id}/export?${queryString}`, 'custom-report.xlsx');
};

// Adhoc endpoints - run/export without saving to database
export const runCustomReportAdhoc = (payload) => api.post('/custom-reports/run-adhoc', payload);

export const exportCustomReportAdhoc = async (payload) => {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}/custom-reports/export-adhoc`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Export failed');
  }
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = (payload.name || 'report').replace(/[^a-zA-Z0-9]/g, '_') + '.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  a.remove();
};

// Secure file download helper — uses Authorization header instead of token in URL
async function downloadFile(url, filename) {
  const token = getToken();
  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Download failed');
  }
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'download';
  // Try to extract filename from Content-Disposition header
  const disposition = response.headers.get('Content-Disposition');
  if (disposition) {
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match) a.download = match[1].replace(/['"]/g, '');
  }
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(blobUrl);
  a.remove();
  // Entries a payroll export's Mark as Paid left unpaid (outside the caller's direct reports)
  return { entriesSkipped: parseInt(response.headers.get('X-Entries-Skipped') || '0', 10) || 0 };
}

// Backup & Restore
export const getBackupSettings = () => api.get('/backup/settings');
export const updateBackupSettings = (data) => api.put('/backup/settings', data);
export const createBackup = (data) => api.post('/backup/create', data);
export const getBackupHistory = (params) => api.get('/backup/history', { params });
export const downloadBackup = (backupId) => downloadFile(`${API_BASE_URL}/backup/download/${backupId}`, 'backup.gz');
export const deleteBackupEntry = (backupId) => api.delete(`/backup/${backupId}`);
export const restoreBackup = (backupId, confirmation) => api.post(`/backup/restore/${backupId}`, { confirmation });
export const restoreBackupFromFile = (formData) => api.post('/backup/restore/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const testSftpConnection = (data) => api.post('/backup/sftp/test', data);

// Work Sites
export const getWorkSites = () => api.get('/work-sites');
export const getWorkSite = (id) => api.get(`/work-sites/${id}`);
export const createWorkSite = (data) => api.post('/work-sites', data);
export const updateWorkSite = (id, data) => api.put(`/work-sites/${id}`, data);
export const deleteWorkSite = (id) => api.delete(`/work-sites/${id}`);
export const getWorkSiteIpRules = (id) => api.get(`/work-sites/${id}/ip-rules`);
export const addWorkSiteIpRule = (id, data) => api.post(`/work-sites/${id}/ip-rules`, data);
export const deleteWorkSiteIpRule = (siteId, ruleId) => api.delete(`/work-sites/${siteId}/ip-rules/${ruleId}`);
export const getWorkSiteEmployees = (id) => api.get(`/work-sites/${id}/employees`);
export const assignWorkSiteEmployees = (id, employeeIds) => api.post(`/work-sites/${id}/employees`, { employeeIds });
export const removeWorkSiteEmployee = (siteId, employeeId) => api.delete(`/work-sites/${siteId}/employees/${employeeId}`);
export const getWorkSiteKioskDevices = (id) => api.get(`/work-sites/${id}/kiosk-devices`);
export const registerKioskDevice = (siteId, data) => api.post(`/work-sites/${siteId}/kiosk-devices`, data);
export const deactivateKioskDevice = (siteId, deviceId) => api.delete(`/work-sites/${siteId}/kiosk-devices/${deviceId}`);
export const getPunchEvents = (params = {}) => api.get('/work-sites/punch-events', { params });
export const punchBreak = (data) => api.post('/clockin/break', data);
export const switchProperty = (data) => api.post('/clockin/switch', data);
export const getPunchOptions = () => api.get('/clockin/punch-options');
export const getEntryPunches = (entryId) => api.get(`/entries/${entryId}/punches`);
export const getPunchPhotoBlob = (entryId, punchId) =>
  api.get(`/entries/${entryId}/punches/${punchId}/photo`, { responseType: 'blob' });
export const getSupportedTaxYears = () => api.get('/payroll/tax-years');

// Employee PIN
export const setEmployeePin = (id, pin) => api.post(`/employees/${id}/set-pin`, { pin });
export const clearEmployeePin = (id) => api.delete(`/employees/${id}/pin`);

// Tasks / Work Orders
export const getTaskSettings = () => api.get('/tasks/settings');
export const updateTaskSettings = (data) => api.put('/tasks/settings', data);
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const updateTaskStatus = (id, data) => api.patch(`/tasks/${id}/status`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
// The assignee's own scheduling of a task — separate from updateTask because
// only the assignee may set it, and the server clears it on reassignment.
export const planTask = (id, plannedStart) => api.put(`/tasks/${id}/plan`, { planned_start: plannedStart });

export default api;
